import { Avatar, Button, IconButton, Tag } from '@ads/components-react';

import { CheckCircleIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

import { useAiBatchSuggestions } from '../../../hooks/useAiBatchSuggestions';
import { useAnswerInfo } from '../../../hooks/useAnswerInfo';
import { formatGrade, normalizeAiGrade } from '../../../utils';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const COLS = 'grid-cols-[1fr_2.5rem] md:grid-cols-[1fr_10rem_2.5rem]';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface StudentSubTableProps {
  /** ID da publicação do exame */
  readonly examPublishId: number;
  /** ID do item de questão (para filtrar os alunos) */
  readonly examItemId: number;
  /** Se false, a query é desabilitada (accordion fechado) */
  readonly enabled?: boolean;
  /**
   * Callback ao clicar em "Corrigir" ou "Visualizar" em um aluno.
   * Recebe o ID da resposta (answerId) e a posição absoluta dela na fila da questão
   * (1-based, contando todas as páginas) — é essa posição que o modal usa para navegar.
   */
  readonly onCorrectStudent: (answerId: number, position: number) => void;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function StudentSubTableSkeleton() {
  return (
    <div className="flex flex-col gap-nano bg-ctx-layout-body p-xxxs">
      <div className={`grid ${COLS} items-center gap-sm px-mini py-nano`}>
        <Skeleton className="h-xxs w-20" />
        <Skeleton className="hidden h-xxs w-16 md:block" />
        <div />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className={`grid ${COLS} h-[4.5rem] items-center gap-sm rounded-md border-hairline border-ctx-layout-border px-xxs py-micro`}
        >
          <div className="flex items-center gap-nano">
            <Skeleton shape="circle" className="size-md" />
            <Skeleton className="h-mini w-48" />
          </div>
          <Skeleton className="hidden h-sm w-16 rounded-pill md:block" />
          <Skeleton shape="circle" className="size-md" />
        </div>
      ))}
    </div>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism StudentSubTable — sub-tabela de alunos para uma questão específica.
 *
 * Exibe os alunos que responderam à questão com seu status de correção
 * (corrigido/pendente) e um botão para abrir o modal de correção diretamente
 * no aluno desejado.
 *
 * @example
 * ```tsx
 * <StudentSubTable
 *   examPublishId={123}
 *   examItemId={456}
 *   enabled={isOpen}
 *   onCorrectStudent={(answerId, position) => openModal(item, answerId, position)}
 * />
 * ```
 */
export function StudentSubTable({
  examPublishId,
  examItemId,
  enabled = true,
  onCorrectStudent,
}: StudentSubTableProps) {
  const { t } = useTranslation('examCorrection');
  const [page, setPage] = useState(1);
  const { get: getAiBatchSuggestion, has: hasAiBatchSuggestion } = useAiBatchSuggestions();

  const { answer, isLoading } = useAnswerInfo(enabled ? examPublishId : null, {
    examItemId,
    pageNumber: page,
    pageSize: PAGE_SIZE,
  });

  const students = answer?.data ?? [];
  const totalPages = answer?.totalPages ?? 1;
  const totalCount = answer?.totalCount ?? 0;

  if (isLoading) {
    return <StudentSubTableSkeleton />;
  }

  if (students.length === 0) {
    return (
      <div className="bg-ctx-layout-body p-xxxs">
        <EmptyState
          compact
          title={t('correction.students.empty')}
          description={t('correction.students.emptyDescription')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-nano bg-ctx-layout-body p-xxxs">
      {/* Sub-header */}
      <div className={`grid ${COLS} items-center gap-sm px-mini py-nano`}>
        <span className="text-ctx-content-title ts-subtitle-xxs">
          {t('correction.students.columns.student')}
        </span>
        <span className="hidden text-ctx-content-title ts-subtitle-xxs md:block">
          {t('correction.students.columns.status')}
        </span>
        <div />
      </div>

      {/* Cards de alunos */}
      {students.map((student, index) => {
        const isCorrected = student.grade !== null && student.grade !== undefined;
        const answerId = student.id ?? 0;
        // Uma sugestão em lote só se sobrepõe ao "Pendente" enquanto a resposta não
        // tiver sido salva — depois de corrigida, "Corrigido" prevalece.
        const hasAiSuggestion = !isCorrected && hasAiBatchSuggestion(answerId);
        // A IA devolve a nota como fração de 0 a 1 — normalizar para a escala 0-100
        // exibida em toda a tela (mesma conversão do painel de IA individual).
        const rawAiSuggestedGrade = getAiBatchSuggestion(answerId)?.suggestedGrade;
        const aiSuggestedGrade =
          rawAiSuggestedGrade !== undefined
            ? formatGrade(normalizeAiGrade(rawAiSuggestedGrade))
            : undefined;

        return (
          <div
            key={student.id ?? index}
            className={`grid ${COLS} h-[4.5rem] items-center gap-sm rounded-md border-hairline border-ctx-layout-border px-xxs py-micro transition-colors`}
          >
            <div className="flex min-w-none items-center gap-nano">
              <Avatar size="md" fallback={(student.student ?? 'A').charAt(0).toUpperCase()} />
              <span className="truncate text-ctx-content-base ts-paragraph-xxs">
                {student.student ?? t('modal.unknownStudent')}
              </span>
            </div>

            <div className="hidden items-center md:flex">
              {isCorrected ? (
                <Tag
                  status="positive"
                  label={t('correction.students.corrected')}
                  custom={{ text: 'text-nowrap' }}
                />
              ) : hasAiSuggestion ? (
                <Tag
                  status="highlight"
                  label={t('correction.students.aiSuggested', {
                    grade: aiSuggestedGrade ?? '—',
                  })}
                  custom={{ text: 'text-nowrap' }}
                />
              ) : (
                <Tag
                  status="helper"
                  label={t('correction.students.pending')}
                  custom={{ text: 'text-nowrap' }}
                />
              )}
            </div>

            <div className="flex items-center justify-end">
              <IconButton
                icon={isCorrected ? EyeIcon : CheckCircleIcon}
                hierarchy="tertiary"
                size="md"
                ariaLabel={
                  isCorrected
                    ? t('correction.students.actions.review')
                    : t('correction.table.actions.correct')
                }
                onClick={() => onCorrectStudent(answerId, (page - 1) * PAGE_SIZE + index + 1)}
              />
            </div>
          </div>
        );
      })}

      {/* Paginação da sub-tabela */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-mini pt-nano">
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('correction.students.showing', {
              count: students.length,
              total: totalCount,
            })}
          </span>
          <div className="flex items-center gap-nano">
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
            >
              {t('dashboard.pagination.previous')}
            </Button>
            <span className="text-ctx-content-base ts-caption-xxs">
              {page} / {totalPages}
            </span>
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
            >
              {t('dashboard.pagination.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
